/**
 * 生成模型注册表（P2-图像-2）。
 *
 * 行业共识（调研 §9.2）：模型下拉 + 参数随模型自适应 + 实时成本预估。这里把「有哪些模型、
 * 各支持什么参数、单价/折扣」收进一张表，面板按所选模型动态渲染，成本行按 模型×分辨率×数量 实时算。
 *
 * 边界（用户 2026-10-01 决策）：这里是**预估显示**用的 mock 价格表；真实 token 扣费/闸门
 * 后置到 billing 里程碑，届时把 baseCredit/discount 换成真单价即可，UI 与算法不变。
 */
import type { GenerationParams } from './canvas';

export interface ModelOption {
  id: string;
  label: string;
  /** 该模型可选分辨率（面板据此显隐，不硬列无关项） */
  resolutions: string[];
  /** 该模型可选画幅 */
  aspectRatios: GenerationParams['aspectRatio'][];
  /** 单次最大生成张数 */
  maxCount: number;
  /** 每图基准积分（以 1080P 计） */
  baseCredit: number;
  /** 折扣系数：1=原价，0.8=8折，0.5=5折 */
  discount: number;
  supportsNegative: boolean;
  supportsReference: boolean;
  note?: string | undefined;
}

/** 图像模型（mock 三个，接真供商时扩充/换表） */
export const IMAGE_MODELS: ModelOption[] = [
  {
    id: 'wan2.7-image',
    label: '通义万相 · 图',
    resolutions: ['720P', '1080P', '2K'],
    aspectRatios: ['16:9', '9:16', '1:1', '3:4', '4:3', '21:9'],
    maxCount: 4,
    baseCredit: 20,
    discount: 1,
    supportsNegative: true,
    supportsReference: true,
    note: '综合 · 中文友好',
  },
  {
    id: 'sd-turbo',
    label: 'SD Turbo（快省）',
    resolutions: ['480P', '720P', '1080P'],
    aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4'],
    maxCount: 4,
    baseCredit: 8,
    discount: 0.5,
    supportsNegative: true,
    supportsReference: false,
    note: '快速出图 · 限时 5 折',
  },
  {
    id: 'flux-pro',
    label: 'Flux Pro（高质）',
    resolutions: ['1080P', '2K', '4K'],
    aspectRatios: ['16:9', '9:16', '1:1', '21:9'],
    maxCount: 2,
    baseCredit: 45,
    discount: 0.8,
    supportsNegative: true,
    supportsReference: true,
    note: '电影级细节 · 8 折',
  },
  {
    id: 'jimeng-4',
    label: '即梦 4.0（中文）',
    resolutions: ['1080P', '2K'],
    aspectRatios: ['1:1', '16:9', '9:16', '3:4', '4:3'],
    maxCount: 4,
    baseCredit: 15,
    discount: 1,
    supportsNegative: true,
    supportsReference: true,
    note: '中文语义强 · 多参考',
  },
  {
    id: 'sdxl-lightning',
    label: 'SDXL Lightning（极速）',
    resolutions: ['720P', '1080P'],
    aspectRatios: ['1:1', '16:9', '9:16'],
    maxCount: 4,
    baseCredit: 6,
    discount: 0.5,
    supportsNegative: true,
    supportsReference: false,
    note: '秒级出图 · 5 折',
  },
];

export function findImageModel(id: string | undefined): ModelOption | undefined {
  return IMAGE_MODELS.find((m) => m.id === id);
}

/** 分辨率相对 1080P 的积分系数（越大越贵） */
export function resolutionFactor(resolution: string): number {
  switch (resolution) {
    case '480P':
      return 0.5;
    case '720P':
      return 0.75;
    case '2K':
      return 1.5;
    case '4K':
      return 2.5;
    default:
      return 1; // 1080P 及未知
  }
}

/** 预计消耗积分（折后）：基准 × 分辨率系数 × 张数 × 折扣，至少 1。纯函数可测。 */
export function estimateImageCredit(model: ModelOption, resolution: string, count: number): number {
  const n = Math.max(1, Math.min(model.maxCount, Math.round(count) || 1));
  return Math.max(1, Math.round(model.baseCredit * resolutionFactor(resolution) * n * model.discount));
}

/** 折扣文案：1→''（原价不显示），0.8→'8折'，0.85→'8.5折' */
export function formatDiscount(discount: number): string {
  if (discount >= 1) {
    return '';
  }
  const v = discount * 10;
  return `${Number.isInteger(v) ? v : v.toFixed(1)}折`;
}

/* ---------- 视频模型（P-D③ 生成台） ---------- */

export interface VideoModel {
  id: string;
  label: string;
  resolutions: string[];
  aspectRatios: GenerationParams['aspectRatio'][];
  /** 可选时长（秒） */
  durations: number[];
  /** 每秒基准积分（以 1080P 计） */
  baseCreditPerSec: number;
  discount: number;
  note?: string | undefined;
}

export const VIDEO_MODELS: VideoModel[] = [
  {
    id: 'wan2.6-i2v',
    label: '通义万相 · 图生视频',
    resolutions: ['480P', '720P', '1080P'],
    aspectRatios: ['16:9', '9:16', '1:1'],
    durations: [5, 10],
    baseCreditPerSec: 12,
    discount: 1,
    note: '参考图驱动 · 中文友好',
  },
  {
    id: 'wan2.6-t2v',
    label: '通义万相 · 文生视频',
    resolutions: ['480P', '720P', '1080P'],
    aspectRatios: ['16:9', '9:16'],
    durations: [5, 10],
    baseCreditPerSec: 10,
    discount: 0.8,
    note: '文字生视频 · 8 折',
  },
  {
    id: 'wan3.0-video',
    label: '万相 3.0 · 长视频',
    resolutions: ['480P', '720P', '1080P'],
    aspectRatios: ['16:9', '9:16', '1:1'],
    durations: [5, 10, 15, 20, 25, 30],
    baseCreditPerSec: 15,
    discount: 1,
    note: '最长 30s · 高一致性',
  },
  {
    id: 'wan2.6-r2v',
    label: '通义万相 · 参考生视频',
    resolutions: ['720P', '1080P'],
    aspectRatios: ['16:9', '9:16', '1:1'],
    durations: [5, 10],
    baseCreditPerSec: 14,
    discount: 1,
    note: '多主体参考驱动 · 保一致性',
  },
  {
    id: 'veo-lite',
    label: '视频编辑模型',
    resolutions: ['720P', '1080P'],
    aspectRatios: ['16:9', '9:16'],
    durations: [5, 10],
    baseCreditPerSec: 16,
    discount: 0.9,
    note: '局部重绘/延长 · 9 折',
  },
];

export function findVideoModel(id: string | undefined): VideoModel | undefined {
  return VIDEO_MODELS.find((m) => m.id === id);
}

/** 视频预计积分：每秒基准 × 分辨率系数 × 时长 × 折扣，至少 1。纯函数可测。 */
export function estimateVideoCredit(model: VideoModel, resolution: string, durationSec: number): number {
  const d = Math.max(1, Math.round(durationSec) || 5);
  return Math.max(1, Math.round(model.baseCreditPerSec * resolutionFactor(resolution) * d * model.discount));
}
